import { inject, Injector } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureFlags } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { EntityManifestService, type EntityManifestOperationKey } from '@workbuddy/services/entities';
import { getEntityBySlug } from '@workbuddy/services/entities/entity.manifest.registry';
import { ENTITY_NO_ACCESS_PATH } from './entity.record.contracts';

/** Runtime counterpart to the create/edit request unions. URLs are untrusted and bypass TypeScript. */
export function isEntityOperationRouteShapeValid(operation: EntityManifestOperationKey | null, id: string | undefined, outlet: string): boolean {

  if (!operation) return false;
  if (operation === 'create') return !id;
  if (!id) return false;
  return outlet === 'dialog' ? operation === 'edit' || operation === 'expand' : operation !== 'expand';
}

export function isEntityRecordFeatureEnabled(featureFlag: FeatureFlags | undefined, flags: Partial<Record<FeatureFlags, boolean>> | undefined): boolean {

  return !featureFlag || flags?.[featureFlag] === true;
}

/**
 * Gates every generic entity route — the full-page record route and the dialog outlet — on the manifest's
 * per-operation authorization.
 *
 * **A guard rather than a check inside each host.** A URL is an untrusted entry point: it can be pasted,
 * bookmarked, or left over from a role the user no longer holds. A component-level check runs after the
 * route has already committed, cannot cancel the navigation, and would be reimplemented per host. This
 * refuses before anything mounts, once, for every route that resolves an entity and an operation.
 *
 * **The route does not load the record.** Every `authorize` in the app is a rights check; none of them
 * read the document. The inspector loads it once, after this gate, and that copy is what the editor
 * draws. Loading it here as well was a second GET of the same item, and the check threw the body away.
 *
 * **This is UX, not security.** Backend mutation guards remain authoritative — anyone can call the API
 * directly. The purpose is to not show people doors they cannot walk through.
 */
export const entityOperationRouteGuard: CanActivateFn = async route => {

  const manifests = inject(EntityManifestService);
  const injector = inject(Injector);
  const router = inject(Router);

  const slug = route.paramMap.get('slug');
  const entity = slug ? getEntityBySlug(slug) : undefined;
  const operation = route.paramMap.get('operation') as EntityManifestOperationKey | null;
  const id = route.paramMap.get('id') ?? undefined;

  // An unroutable URL is refused rather than redirected: there is no entity to send the user to.
  if (!entity || !operation) return false;
  if (!isEntityOperationRouteShapeValid(operation, id, route.outlet)) return false;
  if (!manifests.supportsOperation(entity, operation)) return false;

  // The entity's v2 UI is not rolled out to this tenant: nothing here exists for them, whatever their rights.
  if (!isEntityRecordFeatureEnabled(manifests.getManifest(entity)?.featureFlag, ConfigService.environment.featureFlags)) {
    return route.outlet === 'primary' ? router.parseUrl(`/${ENTITY_NO_ACCESS_PATH}`) : false;
  }

  const routes = manifests.routes(entity);
  if (route.outlet === 'primary' && !isEntityRecordFeatureEnabled(routes?.fullPageFeatureFlag, ConfigService.environment.featureFlags)) {
    return routes?.root ? router.parseUrl(routes.root) : false;
  }

  // Rights only. The inspector loads the document after this returns.
  if (await manifests.authorizeOperation(entity, operation, undefined, injector)) return true;

  // Denied. The entity's board is no destination: the same missing right closes it too. A page says so
  // instead, loading nothing. A secondary outlet just stays closed — an empty dialog announcing what you
  // cannot do is worse than the action being unavailable.
  return route.outlet === 'primary' ? router.parseUrl(`/${ENTITY_NO_ACCESS_PATH}`) : false;
};

/**
 * Rollout gate alone, for the inspector outlet. Rights stay with the inspector host because it may
 * resolve a denied edit to preview, which a rights check here would refuse outright.
 */
export const entityFeatureRouteGuard: CanActivateFn = route => {

  const slug = route.paramMap.get('slug');
  const entity = slug ? getEntityBySlug(slug) : undefined;
  if (!entity) return false;

  return isEntityRecordFeatureEnabled(inject(EntityManifestService).getManifest(entity)?.featureFlag, ConfigService.environment.featureFlags);
};
